import { collection, getDocs } from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { governanceEventSchema, type GovernanceEvent } from "./schemas";

export type GovernanceEventsState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; data: GovernanceEvent[] };

/**
 * Every event, for the one role allowed to see them all.
 *
 * `firestore.rules` grants `list` on events to super admins only — everyone
 * else reaches an event through membership. Governance needs the full list
 * because the event most in need of an admin is a brand new one, which by
 * definition nobody is a member of but its creator.
 */
export function useGovernanceEvents(): GovernanceEventsState & {
  reload: () => void;
} {
  const [state, setState] = useState<GovernanceEventsState>({
    status: "loading",
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    // Unordered for the same reason as useGovernanceUsers: an orderBy excludes
    // documents missing the field, which would hide events from the only
    // surface that can give them an administrator. Sorted client-side.
    getDocs(collection(db, "events"))
      .then((snap) => {
        if (cancelled) return;
        const events: GovernanceEvent[] = [];
        snap.forEach((d) => {
          const result = governanceEventSchema.safeParse({
            eventId: d.id,
            ...d.data(),
          });
          // Fall back to the id rather than dropping the event: an event with
          // no title is exactly the half-created one most likely to need an
          // admin assigned.
          if (result.success)
            events.push({ ...result.data, title: result.data.title || d.id });
        });
        events.sort((a, b) => (a.title ?? "").localeCompare(b.title ?? ""));
        setState({ status: "success", data: events });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          error: err instanceof Error ? err.message : "Failed to load events",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  return { ...state, reload };
}
